// BUILD-042 — Employee workspace section navigation (the secondary rail inside an employee's page).
//
// Two shapes, one component, chosen by `context`:
//   * 'self'  (My HR): the employee's OWN grouped map - Employee / Work & Development / Records (Restricted is never shown, see sectionGroups.ts),
//     expand one group at a time, selected section has a clear current-page state.
//   * 'admin' (HR administering ANOTHER person; Owner decisions 2026-10-24): a compact GROUPED rail under a small identity block (name, hire date, status) -
//     Overview | EMPLOYEE RECORD: Employee details (Personal & contacts and Employment & pay open beneath it), Benefits | ASSIGNMENTS: Onboarding, Documents,
//     Equipment & property | EMPLOYMENT: Offboarding, History - and ONE collapsed "More" group holding every other existing destination (Time off, Training,
//     Reviews, Additional information). Nothing is deleted. Onboarding and Offboarding are the two views of the existing 'checklists' section (?tab=onboarding|offboarding).
// The narrow-screen collapse is done by the shared HrWorkspaceFrame ("Menu" disclosure), so there is no separate mobile <select> here.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SECTION_NAV_GROUPS, ADMIN_EMPLOYEE_FLAT, ADMIN_EMPLOYEE_MORE, groupContaining, isBuilt, labelFor,
  type AdminRailGroupItem, type SectionKey, type WorkspaceViewerContext,
} from './sectionGroups';
import { AttentionBadge } from './AttentionBadge';
import { groupBadgeFor, type BadgeView } from './attentionModel';

interface Props {
  /** Oct 17: optional numbered attention badges per section (already server-derived). Absent/hidden views draw nothing. */
  badges?: Partial<Record<SectionKey, BadgeView>>;
  /** Cycle 13, admin rail only: the Onboarding and Offboarding items each carry THEIR OWN open-task badge (the two views of the one 'checklists' section).
   *  When this is supplied it is used instead of the combined `badges.checklists`, which is never shown on either item; an absent entry draws nothing. */
  checklistBadges?: Partial<Record<'onboarding' | 'offboarding', BadgeView>>;
  active: SectionKey;
  onSelect: (key: SectionKey) => void;
  /** Real per-section visibility - e.g. the caller's resolved HR surfaces/grants. The Restricted group is never shown in the real workspace today
   *  (see sectionGroups.ts's isRestrictedGroup) - callers pass a predicate that returns false for every Restricted key. */
  visible: (key: SectionKey) => boolean;
  /** Which "Planned" set applies - self-service vs. HR Administration viewing an Employee. */
  context: WorkspaceViewerContext;
}

const itemBase = 'w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-md text-sm mb-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]';
const itemClass = (selected: boolean) => `${itemBase} ${selected ? 'bg-[#EDF5FC] text-[#176A9B] font-medium' : 'text-gray-600 hover:bg-gray-50'}`;

function Chevron({ open }: { open: boolean }) {
  return (
    <svg className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

export function WorkspaceNav(props: Props) {
  return props.context === 'admin' ? <AdminEmployeeRail {...props} /> : <SelfRail {...props} />;
}

// ── HR administering another person: a single FLAT readable rail + "More" ───────────────────────────────────────────────────
// Owner decision 2026-10-25 (preview/release parity): no faint uppercase grouping headings, and
// the identity (name, hire date, status) is shown in a SEPARATE employee identity card above this
// rail (rendered by the caller via EmployeeCard), not inside the rail. Every destination, child
// sub-destination and ?tab= deep link is preserved — only the grouping/identity placement changes.
function AdminEmployeeRail({ active, onSelect, visible, badges, checklistBadges, context }: Props) {
  const [params, setParams] = useSearchParams();
  const tabParam: 'onboarding' | 'offboarding' = params.get('tab') === 'offboarding' ? 'offboarding' : 'onboarding';
  const moreKeys = ADMIN_EMPLOYEE_MORE.filter(visible);
  const moreActive = moreKeys.includes(active);
  const [moreOpen, setMoreOpen] = useState(moreActive);
  useEffect(() => { if (moreActive) setMoreOpen(true); }, [moreActive]);

  const items = ADMIN_EMPLOYEE_FLAT.filter((i) => visible(i.section));
  const isSelected = (i: AdminRailGroupItem) => (i.alsoActiveFor ?? [i.section]).includes(active) && (!i.tab || tabParam === i.tab);

  function choose(i: AdminRailGroupItem) {
    if (i.tab) {
      // The tab lives in the URL (the existing ?tab= parameter), so the choice survives a reload and a shared link.
      const next = new URLSearchParams(params);
      next.set('tab', i.tab);
      setParams(next, { replace: true });
    }
    onSelect(i.section);
  }
  const badgeFor = (i: AdminRailGroupItem): BadgeView | undefined => {
    if (i.section === 'documents') return badges?.documents;
    if (i.section === 'checklists') return checklistBadges && i.tab ? checklistBadges[i.tab] : badges?.checklists; // Cycle 13: per-kind when supplied, never the combined number on both
    return badges?.[i.section];
  };

  return (
    <nav aria-label="Employee sections" className="bg-white rounded-lg border border-gray-200 shadow-sm" data-testid="employee-rail-admin">
      <div className="p-2">
        <ul>
          {items.map((i) => {
            const selected = isSelected(i);
            const badge = badgeFor(i);
            const children = (i.children ?? []).filter((c) => visible(c.section));
            const familyActive = children.some((c) => c.section === active);
            return (
              <li key={i.id}>
                {/* A parent whose sub-destination is the current page reads as "you are inside this"; the sub-destination carries the highlight. */}
                <button type="button" onClick={() => choose(i)} aria-current={selected && !familyActive ? 'page' : undefined} aria-expanded={children.length > 0 ? familyActive : undefined} data-testid={`rail-${i.id}`} className={itemClass(selected && !familyActive) + (familyActive ? ' !text-gray-900 !font-medium' : '')}>
                  <span className="min-w-0 break-words">{i.label}</span>
                  {badge && <AttentionBadge view={badge} testId={i.section === 'checklists' && i.tab === 'offboarding' ? 'section-badge-checklists-offboarding' : `section-badge-${i.section}`} />}
                  {!isBuilt(i.section, context) && <span className="text-[10px] text-gray-400 font-normal flex-shrink-0">Planned</span>}
                </button>
                {familyActive && (
                  <ul className="ml-3 pl-2 border-l border-gray-200 mb-0.5" data-testid={`rail-${i.id}-children`}>
                    {children.map((c) => (
                      <li key={c.id}>
                        <button type="button" onClick={() => onSelect(c.section)} aria-current={c.section === active ? 'page' : undefined} data-testid={`rail-sub-${c.id}`} className={itemClass(c.section === active)}>
                          <span className="min-w-0 break-words">{c.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      {moreKeys.length > 0 && (
        <div className="mt-2 pt-1 border-t border-gray-100">
          <button
            type="button"
            data-rail-keep-open
            data-testid="rail-more"
            aria-expanded={moreOpen}
            aria-controls="employee-rail-more"
            onClick={() => setMoreOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
          >
            <span>More</span>
            <Chevron open={moreOpen} />
          </button>
          {moreOpen && (
            <ul id="employee-rail-more" data-testid="rail-more-list" className="mt-0.5">
              {moreKeys.map((key) => {
                const selected = key === active;
                return (
                  <li key={key}>
                    <button type="button" onClick={() => onSelect(key)} aria-current={selected ? 'page' : undefined} data-testid={`rail-more-${key}`} className={itemClass(selected)}>
                      <span className="min-w-0 break-words">{labelFor(key)}</span>
                      {!isBuilt(key, context) && <span className="text-[10px] text-gray-400 font-normal flex-shrink-0">Planned</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      </div>
    </nav>
  );
}

// ── The employee's own rail (My HR): the grouped map, one group open at a time ──────────────────────────────────────────────
function SelfRail({ active, onSelect, visible, context, badges }: Props) {
  const [openGroup, setOpenGroup] = useState<string>(groupContaining(active));
  const visibleGroups = SECTION_NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => visible(i.key)) }))
    .filter((g) => g.items.length > 0);

  return (
    <nav aria-label="Employee sections" className="bg-white rounded-lg border border-gray-200 shadow-sm p-2" data-testid="employee-rail-self">
      <div className="space-y-0.5">
        {visibleGroups.map((g) => {
          const isOpen = openGroup === g.groupLabel;
          return (
            <div key={g.groupLabel}>
              <button
                type="button"
                onClick={() => setOpenGroup(isOpen ? '' : g.groupLabel)}
                className="w-full flex items-center justify-between gap-2 px-2.5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 rounded-md text-left whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
                aria-expanded={isOpen}
              >
                <span className="text-left">{g.groupLabel}</span>
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  {/* A closed group still tells you something inside it needs attention (items inside show their own number). */}
                  {!isOpen && <AttentionBadge view={groupBadgeFor(g.groupLabel, g.items.map((i) => badges?.[i.key]))} testId={`group-badge-${g.groupLabel.replace(/\W+/g, '-').toLowerCase()}`} />}
                  <Chevron open={isOpen} />
                </span>
              </button>
              {isOpen && (
                <ul aria-label={g.groupLabel} className="mb-1.5">
                  {g.items.map((item) => {
                    const selected = item.key === active;
                    return (
                      <li key={item.key}>
                        <button
                          type="button"
                          aria-current={selected ? 'page' : undefined}
                          onClick={() => { onSelect(item.key); setOpenGroup(g.groupLabel); }}
                          className={itemClass(selected)}
                        >
                          <span className="min-w-0 break-words">{item.label}</span>
                          {badges?.[item.key] && <AttentionBadge view={badges[item.key]!} testId={`section-badge-${item.key}`} />}
                          {!isBuilt(item.key, context) && (
                            <span className="text-[10px] text-gray-400 font-normal flex-shrink-0">Planned</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
